import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Textarea,
} from '@repo/ui'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, GitMerge, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import { useDuplicateLeads, useMergeContacts } from '@/hooks/use-leads'
import type {
  ContactSource,
  ContactStatus,
  DuplicateLeadContact,
  DuplicateLeadGroup,
} from '@/types/leads'

export const Route = createFileRoute('/_app/leads/duplicados')({
  component: DuplicateLeadsPage,
})

const EMPTY = '__empty__'

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  email: 'Email',
  phone: 'Telefone',
  company: 'Empresa',
  status: 'Status',
  source: 'Origem',
  ownerUserId: 'Responsável',
  rdStationId: 'RD Station',
  stageId: 'Etapa',
}

function valueLabel(value: string | null | undefined) {
  return value?.trim() || 'Vazio'
}

function optionValue(value: string | null | undefined) {
  return value?.trim() || EMPTY
}

function fromOptionValue(value: string) {
  return value === EMPTY ? null : value
}

function enumOption<T extends string>(value: string) {
  return value === EMPTY ? undefined : (value as T)
}

function uniqueOptions(values: Array<string | null | undefined>) {
  const seen = new Set<string>()
  return values.filter((value) => {
    const key = optionValue(value)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function contactTitle(contact: DuplicateLeadContact) {
  return contact.name || contact.phone || contact.email || 'Lead'
}

function countLabel(contact: DuplicateLeadContact) {
  return [
    `${contact._count.conversations} atend.`,
    `${contact._count.appointments} reun.`,
    `${contact._count.cadenceEnrollments} cad.`,
    `${contact.tags.length} tags`,
  ].join(' / ')
}

function buildInitialChoices(group: DuplicateLeadGroup, primaryId: string) {
  const primary = group.contacts.find((contact) => contact.id === primaryId)
  const fallback = primary ?? group.contacts[0]

  if (!fallback) {
    return {
      name: EMPTY,
      email: EMPTY,
      phone: EMPTY,
      company: EMPTY,
      status: EMPTY,
      source: EMPTY,
      ownerUserId: EMPTY,
      rdStationId: EMPTY,
      stageId: EMPTY,
    }
  }

  return {
    name: optionValue(fallback.name),
    email: optionValue(fallback.email),
    phone: optionValue(fallback.phone),
    company: optionValue(fallback.company),
    status: optionValue(fallback.status),
    source: optionValue(fallback.source),
    ownerUserId: optionValue(fallback.ownerUserId),
    rdStationId: optionValue(fallback.rd_station_id),
    stageId: optionValue(fallback.kanbanCard?.stageId),
  }
}

function buildCustomChoices(group: DuplicateLeadGroup, primaryId: string) {
  const primary = group.contacts.find((contact) => contact.id === primaryId)
  const choices: Record<string, string> = {}

  for (const field of customFieldRows(group)) {
    const primaryValue = primary?.customFields.find(
      (item) => item.customField.id === field.id
    )?.value
    choices[field.id] = optionValue(primaryValue ?? field.values[0] ?? null)
  }

  return choices
}

function customFieldRows(group: DuplicateLeadGroup) {
  const byId = new Map<
    string,
    {
      id: string
      name: string
      values: Array<string | null>
    }
  >()

  for (const contact of group.contacts) {
    for (const field of contact.customFields) {
      const row = byId.get(field.customField.id) ?? {
        id: field.customField.id,
        name: field.customField.name,
        values: [],
      }
      row.values.push(field.value)
      byId.set(field.customField.id, row)
    }
  }

  return Array.from(byId.values()).filter(
    (row) => uniqueOptions(row.values).length > 1
  )
}

function selectedFieldRows(
  group: DuplicateLeadGroup,
  choices: Record<string, string>
) {
  return (
    [
      'name',
      'email',
      'phone',
      'company',
      'status',
      'source',
      'ownerUserId',
      'rdStationId',
      'stageId',
    ] as Array<keyof ReturnType<typeof buildInitialChoices>>
  ).map((field) => {
    const selectedValue = fromOptionValue(choices[field] ?? EMPTY)
    const stage = group.contacts.find(
      (contact) => contact.kanbanCard?.stageId === selectedValue
    )?.kanbanCard?.stage

    return {
      field,
      label: FIELD_LABELS[field],
      value:
        field === 'stageId' && stage ? stage.name : valueLabel(selectedValue),
    }
  })
}

function FieldChoice({
  field,
  group,
  value,
  onChange,
}: {
  field: keyof ReturnType<typeof buildInitialChoices>
  group: DuplicateLeadGroup
  value: string
  onChange: (value: string) => void
}) {
  const values = uniqueOptions(
    group.contacts.map((contact) => {
      if (field === 'rdStationId') return contact.rd_station_id
      if (field === 'stageId') return contact.kanbanCard?.stageId
      return contact[field as keyof DuplicateLeadContact] as string | null
    })
  )
  const hasConflict = values.length > 1

  return (
    <div className="grid gap-2 border-b py-3 md:grid-cols-[140px_1fr]">
      <div>
        <div className="text-sm font-medium">{FIELD_LABELS[field]}</div>
        {hasConflict ? (
          <Badge variant="secondary" className="mt-1">
            conflito
          </Badge>
        ) : null}
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Selecionar valor" />
        </SelectTrigger>
        <SelectContent>
          {values.map((item) => {
            const itemValue = optionValue(item)
            const stage = group.contacts.find(
              (contact) => contact.kanbanCard?.stageId === item
            )?.kanbanCard?.stage
            return (
              <SelectItem key={itemValue} value={itemValue}>
                {field === 'stageId' && stage ? stage.name : valueLabel(item)}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}

function DuplicateLeadsPage() {
  const { currentOrgId } = useAuth()
  const navigate = useNavigate()
  const { data: groups = [], isLoading } = useDuplicateLeads(currentOrgId)
  const mergeContacts = useMergeContacts(currentOrgId)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === selectedGroupId) ?? groups[0],
    [groups, selectedGroupId]
  )
  const [primaryId, setPrimaryId] = useState('')
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [customChoices, setCustomChoices] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    if (!selectedGroup) return
    setSelectedGroupId(selectedGroup.id)
    const nextPrimaryId = selectedGroup.contacts[0]?.id ?? ''
    setPrimaryId(nextPrimaryId)
    setChoices(buildInitialChoices(selectedGroup, nextPrimaryId))
    setCustomChoices(buildCustomChoices(selectedGroup, nextPrimaryId))
    setNotes(selectedGroup.contacts[0]?.notes ?? '')
    // Reinicia o formulário só quando muda o grupo selecionado (pelo id) — intencional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id])

  useEffect(() => {
    if (!selectedGroup || !primaryId) return
    setChoices(buildInitialChoices(selectedGroup, primaryId))
    setCustomChoices(buildCustomChoices(selectedGroup, primaryId))
    setNotes(
      selectedGroup.contacts.find((contact) => contact.id === primaryId)
        ?.notes ?? ''
    )
    // Recalcula só quando muda o contato primário escolhido — intencional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryId])

  async function mergeSelectedGroup() {
    if (!selectedGroup || !primaryId) return

    const duplicateContactIds = selectedGroup.contacts
      .map((contact) => contact.id)
      .filter((id) => id !== primaryId)

    await mergeContacts.mutateAsync({
      primaryContactId: primaryId,
      duplicateContactIds,
      fieldValues: {
        name: fromOptionValue(choices.name ?? EMPTY),
        email: fromOptionValue(choices.email ?? EMPTY),
        phone: fromOptionValue(choices.phone ?? EMPTY),
        company: fromOptionValue(choices.company ?? EMPTY),
        notes,
        status: enumOption<ContactStatus>(choices.status ?? EMPTY),
        source: enumOption<ContactSource>(choices.source ?? EMPTY),
        ownerUserId: fromOptionValue(choices.ownerUserId ?? EMPTY),
        rdStationId: fromOptionValue(choices.rdStationId ?? EMPTY),
        stageId: fromOptionValue(choices.stageId ?? EMPTY),
        customFields: Object.fromEntries(
          Object.entries(customChoices).map(([id, value]) => [
            id,
            fromOptionValue(value),
          ])
        ),
      },
    })

    toast.success('Leads mesclados')
    setConfirmOpen(false)
    void navigate({ to: '/leads/$leadId', params: { leadId: primaryId } })
  }

  if (!currentOrgId) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center">
        Selecione uma organização
      </div>
    )
  }

  return (
    <div className="bg-muted/40 flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={GitMerge}
        title="Possíveis duplicados"
        description="Revise suspeitas e escolha os dados finais antes de mesclar"
        back={
          <>
            <Button variant="outline" size="icon" asChild>
              <Link to="/leads">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
          </>
        }
        meta={<Badge variant="secondary">{groups.length}</Badge>}
      />

      {isLoading ? (
        <div className="space-y-3 p-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <Users className="text-muted-foreground h-10 w-10" />
          <div>
            <h2 className="font-semibold">Nenhum duplicado encontrado</h2>
            <p className="text-muted-foreground text-sm">
              Não há leads com email, RD ou telefone equivalente repetido.
            </p>
          </div>
        </div>
      ) : (
        <main className="grid min-h-0 flex-1 gap-4 p-4 lg:grid-cols-[360px_1fr]">
          <aside className="bg-card min-h-0 overflow-auto rounded-md border">
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                className={`hover:bg-muted w-full border-b p-4 text-left transition ${
                  selectedGroup?.id === group.id ? 'bg-muted' : ''
                }`}
                onClick={() => setSelectedGroupId(group.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">
                    {group.contacts.length} leads parecidos
                  </div>
                  <Badge variant="secondary">{group.reasons[0]}</Badge>
                </div>
                <div className="text-muted-foreground mt-2 text-sm">
                  {group.contacts.map(contactTitle).join(' / ')}
                </div>
              </button>
            ))}
          </aside>

          {selectedGroup ? (
            <section className="bg-card min-h-0 overflow-auto rounded-md border">
              <div className="border-b p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Resolver grupo de duplicados
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Motivos: {selectedGroup.reasons.join(', ')}
                    </p>
                  </div>
                  <Button
                    type="button"
                    disabled={mergeContacts.isPending}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {mergeContacts.isPending ? (
                      <Spinner />
                    ) : (
                      <GitMerge className="h-4 w-4" />
                    )}
                    Mesclar
                  </Button>
                </div>
              </div>

              <div className="grid gap-4 p-5 xl:grid-cols-[320px_1fr]">
                <div className="space-y-3">
                  <div className="text-sm font-medium">Lead principal</div>
                  {selectedGroup.contacts.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      className={`hover:bg-muted w-full rounded-md border p-3 text-left transition ${
                        primaryId === contact.id
                          ? 'border-primary bg-primary/5'
                          : ''
                      }`}
                      onClick={() => setPrimaryId(contact.id)}
                    >
                      <div className="font-medium">{contactTitle(contact)}</div>
                      <div className="text-muted-foreground mt-1 text-xs">
                        {contact.phone || 'Sem telefone'} /{' '}
                        {contact.email || 'Sem email'}
                      </div>
                      <div className="text-muted-foreground mt-2 text-xs">
                        {countLabel(contact)}
                      </div>
                    </button>
                  ))}
                </div>

                <div>
                  <div className="rounded-md border px-4">
                    {(
                      [
                        'name',
                        'email',
                        'phone',
                        'company',
                        'status',
                        'source',
                        'ownerUserId',
                        'rdStationId',
                        'stageId',
                      ] as Array<keyof ReturnType<typeof buildInitialChoices>>
                    ).map((field) => (
                      <FieldChoice
                        key={field}
                        field={field}
                        group={selectedGroup}
                        value={choices[field] ?? EMPTY}
                        onChange={(value) =>
                          setChoices((current) => ({
                            ...current,
                            [field]: value,
                          }))
                        }
                      />
                    ))}

                    <div className="grid gap-2 py-3 md:grid-cols-[140px_1fr]">
                      <div className="text-sm font-medium">Notas</div>
                      <Textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        rows={5}
                      />
                    </div>
                  </div>

                  <p className="text-muted-foreground mt-2 text-xs">
                    Consentimento não é escolhido na mesclagem: o histórico dos
                    contatos é unido e, se algum deles estiver em opt-out, o
                    contato mesclado fica em opt-out.
                  </p>

                  {customFieldRows(selectedGroup).length > 0 ? (
                    <div className="mt-4 rounded-md border px-4">
                      <div className="border-b py-3 text-sm font-medium">
                        Campos personalizados com conflito
                      </div>
                      {customFieldRows(selectedGroup).map((field) => (
                        <div
                          key={field.id}
                          className="grid gap-2 border-b py-3 last:border-b-0 md:grid-cols-[180px_1fr]"
                        >
                          <div className="text-sm font-medium">
                            {field.name}
                          </div>
                          <Select
                            value={customChoices[field.id] ?? EMPTY}
                            onValueChange={(value) =>
                              setCustomChoices((current) => ({
                                ...current,
                                [field.id]: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecionar valor" />
                            </SelectTrigger>
                            <SelectContent>
                              {uniqueOptions(field.values).map((item) => (
                                <SelectItem
                                  key={optionValue(item)}
                                  value={optionValue(item)}
                                >
                                  {valueLabel(item)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}
        </main>
      )}
      {selectedGroup ? (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-auto">
            <DialogHeader>
              <DialogTitle>Confirmar mesclagem de leads</DialogTitle>
              <DialogDescription>
                Revise o lead final e os registros que serao incorporados antes
                de confirmar.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-md border p-3">
                  <div className="text-sm font-medium">Lead que vai ficar</div>
                  {selectedGroup.contacts
                    .filter((contact) => contact.id === primaryId)
                    .map((contact) => (
                      <div key={contact.id} className="mt-2 text-sm">
                        <div className="font-semibold">
                          {contactTitle(contact)}
                        </div>
                        <div className="text-muted-foreground">
                          {contact.phone || 'Sem telefone'} /{' '}
                          {contact.email || 'Sem email'}
                        </div>
                        <div className="text-muted-foreground mt-1 text-xs">
                          {countLabel(contact)}
                        </div>
                      </div>
                    ))}
                </div>

                <div className="rounded-md border p-3">
                  <div className="text-sm font-medium">
                    Leads que serao removidos
                  </div>
                  <div className="mt-2 space-y-2">
                    {selectedGroup.contacts
                      .filter((contact) => contact.id !== primaryId)
                      .map((contact) => (
                        <div key={contact.id} className="text-sm">
                          <div className="font-semibold">
                            {contactTitle(contact)}
                          </div>
                          <div className="text-muted-foreground">
                            {contact.phone || 'Sem telefone'} /{' '}
                            {contact.email || 'Sem email'}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {countLabel(contact)}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              <div className="rounded-md border">
                <div className="border-b px-3 py-2 text-sm font-medium">
                  Dados finais do lead
                </div>
                <div className="divide-y">
                  {selectedFieldRows(selectedGroup, choices).map((row) => (
                    <div
                      key={row.field}
                      className="grid gap-2 px-3 py-2 text-sm md:grid-cols-[160px_1fr]"
                    >
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="font-medium">{row.value}</span>
                    </div>
                  ))}
                  <div className="grid gap-2 px-3 py-2 text-sm md:grid-cols-[160px_1fr]">
                    <span className="text-muted-foreground">Notas</span>
                    <span className="font-medium whitespace-pre-wrap">
                      {notes.trim() || 'Vazio'}
                    </span>
                  </div>
                </div>
              </div>

              {customFieldRows(selectedGroup).length > 0 ? (
                <div className="rounded-md border">
                  <div className="border-b px-3 py-2 text-sm font-medium">
                    Campos personalizados finais
                  </div>
                  <div className="divide-y">
                    {customFieldRows(selectedGroup).map((field) => (
                      <div
                        key={field.id}
                        className="grid gap-2 px-3 py-2 text-sm md:grid-cols-[220px_1fr]"
                      >
                        <span className="text-muted-foreground">
                          {field.name}
                        </span>
                        <span className="font-medium">
                          {valueLabel(
                            fromOptionValue(customChoices[field.id] ?? EMPTY)
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmOpen(false)}
              >
                Revisar
              </Button>
              <Button
                type="button"
                disabled={mergeContacts.isPending}
                onClick={() => void mergeSelectedGroup()}
              >
                {mergeContacts.isPending ? (
                  <Spinner />
                ) : (
                  <GitMerge className="h-4 w-4" />
                )}
                Confirmar mesclagem
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}
